import {
  ChatBubbleBottomCenterTextIcon,
  CheckBadgeIcon,
  HeartIcon,
  KeyIcon,
  LightBulbIcon,
  NewspaperIcon,
  ShareIcon,
  UserGroupIcon,
} from "@heroicons/react/24/solid";
import { FlagCountResponse, GroupCount, TopicCount } from "../../api/Items";
import { useReportDetail } from "../ReportDetailProvider";
import { ITEM_SOURCE_AFFILIATION } from "../../affiliation/helpers";
import { useLensTopicsEnabled } from "./useLensTopicsEnabled";

export const FlagsSummary = ({
  flagsCount,
  keywordCount,
  topicCount,
}: {
  flagsCount?: FlagCountResponse;
  keywordCount?: GroupCount;
  topicCount?: TopicCount;
}) => {
  const { blueprint } = useReportDetail();
  const lensTopicsEnabled = useLensTopicsEnabled();
  const postsCount =
    flagsCount?.item_types.groups.find(
      (group) => group.name === "social_media_post"
    )?.count ?? 0;
  const articlesCount =
    flagsCount?.item_types.groups.find((group) => group.name === "web_content")
      ?.count ?? 0;
  const keywordsCount = keywordCount?.total ?? 0;
  const topicsCount = topicCount?.total ?? 0;

  const originalCount =
    flagsCount?.post_types.groups.find((group) => group.name === "original")
      ?.count ?? 0;
  const sharedCount =
    flagsCount?.post_types.groups.find((group) => group.name === "shared")
      ?.count ?? 0;
  const likedCount =
    flagsCount?.post_types.groups.find((group) => group.name === "liked")
      ?.count ?? 0;

  const affiliationCount =
    flagsCount?.item_sources.groups.find(
      (group) => group.name === ITEM_SOURCE_AFFILIATION
    )?.count ?? 0;

  const itemTypes = [
    {
      label: "posts",
      count: postsCount,
      icon: <ChatBubbleBottomCenterTextIcon className="w-6 h-6" />,
    },
    {
      label: "articles",
      count: articlesCount,
      icon: <NewspaperIcon className="w-6 h-6" />,
    },
  ];

  const hasAffiliationType = blueprint?.item_sources?.find(
    (source) => source.name === ITEM_SOURCE_AFFILIATION
  );
  if (hasAffiliationType) {
    itemTypes.push({
      label: "affiliation",
      count: affiliationCount,
      icon: <UserGroupIcon className="w-6 h-6" />,
    });
  }

  itemTypes.push({
    label: "keywords",
    count: keywordsCount,
    icon: <KeyIcon className="w-6 h-6" />,
  });
  if (lensTopicsEnabled) {
    itemTypes.push({
      label: "topics",
      count: topicsCount,
      icon: <LightBulbIcon className="w-6 h-6" aria-label="Topics" />,
    });
  }

  const postTypes = [
    {
      label: "original",
      count: originalCount,
      icon: <CheckBadgeIcon className="w-6 h-6" />,
    },
    {
      label: "liked",
      count: likedCount,
      icon: <HeartIcon className="w-6 h-6" />,
    },
    {
      label: "shared",
      count: sharedCount,
      icon: <ShareIcon className="w-6 h-6" />,
    },
  ];

  return (
    <div
      data-testid="flagged-content-summary-bar"
      className="flex text-base font-semibold text-secondaryDarkBlue mb-2 rounded-md bg-gray-50 px-6 py-2 flex-wrap gap-x-4 gap-y-2"
    >
      {itemTypes.map((p) => (
        <div
          key={p.label}
          data-testid={`summary-metric-${p.label}`}
          className="flex items-center gap-2 flex-grow-0 shrink-0 whitespace-nowrap"
        >
          <span>{p.icon}</span>
          <span className="capitalize">{p.label}:</span>
          <span>{p.count}</span>
        </div>
      ))}
      {postTypes.map((p) => (
        <div
          key={p.label}
          className="flex items-center gap-2 shrink-0 whitespace-nowrap"
        >
          <span>{p.icon}</span>
          <span className="capitalize">{p.label}:</span>
          <span>{p.count}</span>
        </div>
      ))}
    </div>
  );
};
